import { OnInit, ViewChild, ElementRef, ViewChildren, QueryList, OnDestroy, TemplateRef, HostBinding, Directive } from '@angular/core';
import { AtpBasePageComponent } from '../../atp-base-page-component';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent, MatTab } from '@angular/material/tabs';
import { IAtpAddEditPropertyInfo, AtpAddEditData, AtpValidators, IAtpAddEditInfo, IAtpSelectItem, AppInjector, IAtpAddEditGroupPropertyInfo, IAtpAnnotations } from '../../atp-core';
import { Router } from '@angular/router';
import { FormGroup, ValidatorFn, FormControl, AbstractControl } from '@angular/forms';
import { AtpActionsPopup, AtpActionsPopupData } from '../../components/atp-actions-popup/atp-actions-popup.component';
import { AtpHttpService, IAtpUser } from '../../services/atp-http.service';
import { Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AtpDateTimeService } from '../../components/atp-range-date-time-picker/atp-date-time.service';
import { AtpHttpErrorsService, AtpHttpErrorHandler } from '../../services/atp-http-errors.service';
import { Title } from '@angular/platform-browser';

interface IInvisibleGroup {
  name: string;
  groupName: string;
  items: IInvisibleControl[];
}

interface IInvisibleControl {
  name: string;
  controlType: string;
  params: any[];
  control: AbstractControl;
  group?: IAtpAddEditGroupPropertyInfo;
}

@Directive()
export abstract class AtpDynamicFormComponent<T extends AtpAddEditData<any, IAtpAddEditInfo>> extends AtpBasePageComponent implements OnInit, OnDestroy {
  protected titleService: Title = AppInjector.get(Title);
  protected dateTimeService: AtpDateTimeService = AppInjector.get(AtpDateTimeService);

  constructor(data: T, protected api: AtpHttpService<IAtpUser>, protected httpErrors: AtpHttpErrorsService, protected dialog: MatDialog, protected router: Router) {
    super();
    this.data = data;
    this._oldTitle = this.titleService.getTitle();
  }

  @HostBinding('@.disabled') private _animationDisabled = true;

  private _data: T;
  get data(): T {
    return this._data;
  }
  set data(value: T) {
    if (value) {
      this._animationDisabled = true;
      setTimeout(() => {
        this._animationDisabled = false;
      }, 0);
    }
    this._data = value;
  }

  private _oldTitle: string;

  fullHeightContent = false;

  getTemplateRef(templateRefs: { [key: string]: TemplateRef<any> }, controlType: string): TemplateRef<any> {
    if (controlType == 'File') {
      controlType = 'MultipleFile';
    }
    else if (controlType.includes('Date') || controlType.includes('Time')) {
      controlType = 'RangeDateTime';
    }
    else if (controlType == 'Number') {
      controlType = 'Input';
    }
    return templateRefs[controlType];
  }

  get annotations(): IAtpAnnotations {
    return (<any>this.constructor).__atpannotations__;
  }

  form: FormGroup;
  allControls: { [key: string]: AbstractControl } = {};

  protected invisibleControls: IInvisibleGroup[] = [];

  items: Array<IAtpAddEditPropertyInfo | IAtpAddEditGroupPropertyInfo>;

  masks: { [key: string]: Array<string | RegExp> } = {};

  autocompletes: { [key: string]: Array<IAtpSelectItem> } = {};
  multipleAutocompleteForm = new FormGroup({});

  multipleAutocompleteRemove(name: string, item: any) {
    this.form.controls[name].value.splice(this.form.controls[name].value.findIndex(x => x.id == item.id), 1);
    this.form.controls[name].setValue(this.form.controls[name].value);
  }

  multipleAutocompleteSelected(name: string, event: MatAutocompleteSelectedEvent) {
    this.autocompletes[name] = [];
    this.form.controls[name].setValue(this.form.controls[name].value ? [...this.form.controls[name].value, event.option.value] : [event.option.value]);
  }

  protected autocomplete(name: string, value: string, isMultiple = false) {
    (<Observable<IAtpSelectItem[]>>this.api['autocomplete' + name.substr(0, 1).toUpperCase() + name.substr(1)](value, isMultiple ? this.form.controls[name].value ? this.form.controls[name].value.map(x => x.id) : null : null)).subscribe(
      (data: IAtpSelectItem[]) => {
        for (const item of data) {
          (<any>item).toString = (): string => { return item.value };
        };
        this.autocompletes[name] = data;
      },
      err => {
        this.httpErrors.process(err.status, () => { this.autocomplete(name, value, isMultiple); }, null, [new AtpHttpErrorHandler(400, err.error)]);
      }
    );
  }

  protected autocompleteBlur(name: string) {
    if (typeof (this.form.controls[name].value) == 'string') {
      this.form.controls[name].setValue('');
    }
  }

  protected getItemsFormControlsForInvisibleControls(items: Array<IAtpAddEditPropertyInfo | IAtpAddEditGroupPropertyInfo>, group?: IAtpAddEditGroupPropertyInfo): IInvisibleControl[] {
    let result = [];
    for (const item of items) {
      if ((<any>item).items) {
        result.push(...this.getItemsFormControlsForInvisibleControls((<any>item).items, (<IAtpAddEditGroupPropertyInfo>item).bind ? <IAtpAddEditGroupPropertyInfo>item : null));
      }
      else {
        let newItem: IInvisibleControl;

        const name = item.name;
        if (!this.form.controls[name] && group) {
          newItem = this.invisibleControls.find(x => x.name == group.name).items.find(x => x.name == name);
        }
        else {
          newItem = {
            name: item.name,
            controlType: (<IAtpAddEditPropertyInfo>item).controlType,
            params: (<IAtpAddEditPropertyInfo>item).params,
            control: this.form.controls[name],
            group: group
          };
        }

        if (!newItem.control) {
          throw 'DynamicForms getItemsFormControlsForInvisibleControls control is undefined';
        }
        result.push(newItem);
      }
    }
    return result;
  }

  protected getGroupByName(name: string, groups?: IAtpAddEditGroupPropertyInfo[]): IAtpAddEditGroupPropertyInfo {
    if (!groups) {
      groups = this.data.info.groups;
    }

    let result: IAtpAddEditGroupPropertyInfo;
    for (const group of groups) {
      if (group.name == name) {
        result = group;
        break;
      }
      else {
        if (group.items && group.items) {
          const nextGroups = <IAtpAddEditGroupPropertyInfo[]>group.items.filter(x => (<IAtpAddEditGroupPropertyInfo>x).items);
          if (nextGroups.length > 0) {
            result = this.getGroupByName(name, nextGroups);
            if (result) {
              break;
            }
          }
        }
      }
    }
    return result;
  }

  @ViewChildren('tabItem') set tabItems(value: QueryList<MatTab>) {
    setTimeout(() => {
      for (const group of value.toArray()) {
        const info: IAtpAddEditGroupPropertyInfo = <any>group.textLabel;
        const parentInfo: IAtpAddEditGroupPropertyInfo = this.getGroupByName(info.groupName);

        if (!parentInfo || !parentInfo.params || !parentInfo.params.length || (parentInfo.params[0] != 'OnlyActive' && this.invisibleControls.find(x => x.name == info.name))) {
          continue;
        }

        let invisibleControl = this.invisibleControls.find(x => x.name == info.name);

        if (!invisibleControl) {
          invisibleControl = {
            name: info.name,
            groupName: info.groupName,
            items: this.getItemsFormControlsForInvisibleControls(info.items, info)
          };
          this.invisibleControls.push(invisibleControl);
        }

        if (!group.isActive) {
          info.isVisible = false;

          this.removeInvisibleControls(invisibleControl.items);
        }
        else {
          info.isVisible = true;
        }
      }
    }, 0);
  }

  protected addInvisibleControls(items: IInvisibleControl[]) {
    for (const item of items) {
      if (!item.group || item.group.isVisible) {
        this.form.addControl(item.name, item.control);
      }
    }
  }

  protected removeInvisibleControls(items: IInvisibleControl[]) {
    for (const item of items) {
      this.form.removeControl(item.name);
    }
  }

  protected resetBindingGroupInTabItem(groups: IAtpAddEditGroupPropertyInfo[]) {
    for (const group of groups) {
      (group).isVisible = false;
      setTimeout(() => {
        (group).isVisible = true;
      }, 0);
    }
  }

  protected onMatTabChangeEvent(event: MatTabChangeEvent) {
    const info = <any>event.tab.textLabel;
    info.isVisible = true;
    const groups = this.invisibleControls.filter(x => x.groupName == info.groupName && x.name != info.name);
    if (!groups || !groups.length) return;
    for (const group of groups) {
      this.getGroupByName(group.name).isVisible = false;
      this.removeInvisibleControls(group.items);
    }

    this.addInvisibleControls(this.invisibleControls.find(x => x.name == info.name).items);

    this.resetBindingGroupInTabItem(info.items.filter(x => x.bind && x.isVisible));
  }

  /**
   * Заполнение групп, корректировка значений
   * @param info
   */
  protected correctPropertiesInfo(info: IAtpAddEditInfo) {
    for (const group of info.groups) {
      if (group.type == 'Tabs' && group.name == 'Root') {
        // this.fullHeightContent = true;
      }

      group.items = info.properties.filter(x => x.groupName == group.name);
      if (group.type == 'TabItem') {
        group.toString = () => group.display;
        group.form = this.form;
      }
    }

    for (const group of info.groups) {
      if (group.groupName) {
        const parent = info.groups.find(x => x.name == group.groupName);
        parent.items = parent.items.concat([group]);
      }
      if (group.bind) {
        const arrBind = (group.bind.substr(0, 1).toLowerCase() + group.bind.substr(1)).split(': ');

        let invisibleItems = this.getItemsFormControlsForInvisibleControls(group.items, group);
        this.invisibleControls.push({ name: group.name, groupName: group.groupName, items: invisibleItems });

        let name = arrBind[0][0] == '!' ? arrBind[0].substr(1) : arrBind[0];
        name = name.substr(0, 1).toLowerCase() + name.substr(1);

        const getSelectsPropertyName: () => string = () => {
          const selectsPropertyName = arrBind[1].substr(0, arrBind[1].indexOf('{') - 1);
          return selectsPropertyName.substr(0, 1).toLowerCase() + selectsPropertyName.substr(1);
        };

        const getSelectsPropertyValue: () => string = () => {
          const value = arrBind[1].substr(arrBind[1].indexOf('{'));
          return value.substr(1, value.length - 2);
        };

        let formControl = this.form.controls[name] || this.invisibleControls.find(x => !!x.items.find(y => y.name == name)).items.find(x => x.name == name).control;

        let bindPropertyInfo = info.properties.find(x => x.name == name);
        let isVisible = arrBind.length == 1 ? !!formControl.value : false;
        if (arrBind.length > 1) {
          switch (bindPropertyInfo.controlType) {
            case 'MultipleSelect':
              {
                let baseItem = bindPropertyInfo.selectItems.find(x => x[getSelectsPropertyName()] == getSelectsPropertyValue());
                isVisible = baseItem && formControl.value && formControl.value.find(x => x == baseItem.id);
              }
              break;
            case 'Select':
              {
                let baseItem = bindPropertyInfo.selectItems.find(x => x[getSelectsPropertyName()] == getSelectsPropertyValue());
                isVisible = baseItem && formControl.value && formControl.value == baseItem.id;
              }
              break;
            case 'MultipleAutocomplete':
              isVisible = formControl.value && formControl.value.find(x => x[getSelectsPropertyName()] == getSelectsPropertyValue());
              break;
            case 'Autocomplete':
              isVisible = formControl.value && formControl.value[getSelectsPropertyName()] == getSelectsPropertyValue();
              break;
          }
        }

        const visibleAction: () => void = () => {
          if (group.isVisible) this.addInvisibleControls(invisibleItems);
          else this.removeInvisibleControls(invisibleItems);
        };

        group.isVisible = arrBind[0][0] == '!' ? !isVisible : isVisible;
        visibleAction();

        formControl.valueChanges.subscribe(
          (value: any) => {
            if (arrBind.length == 1) {
              group.isVisible = arrBind[0][0] == '!' ? !value : value;
            }
            else {
              switch (bindPropertyInfo.controlType) {
                case 'MultipleSelect':
                  {
                    let baseItem = bindPropertyInfo.selectItems.find(x => x[getSelectsPropertyName()] == getSelectsPropertyValue());
                    if (baseItem) {
                      group.isVisible = value.find(x => x == baseItem.id);
                    }
                  }
                  break;
                case 'Select':
                  {
                    let baseItem = bindPropertyInfo.selectItems.find(x => x[getSelectsPropertyName()] == getSelectsPropertyValue());
                    if (baseItem) {
                      group.isVisible = value == baseItem.id;
                    }
                  }
                  break;
                case 'MultipleAutocomplete':
                  {
                    group.isVisible = !!value.find(x => x[getSelectsPropertyName()] == getSelectsPropertyValue());
                  }
                  break;
                case 'Autocomplete':
                  {
                    group.isVisible = value[getSelectsPropertyName()] == getSelectsPropertyValue();
                  }
                  break;
              }
            }
            visibleAction();
          }
        );
      }
      else {
        group.isVisible = true;
      }
    }

    for (const group of info.groups) {
      group.items.sort((x, y) => x.order > y.order ? 1 : x.order < y.order ? -1 : 0);
    }

    this.items = [].concat(this.data.info.properties, this.data.info.groups)
      .filter(x => !x.groupName)
      .sort((x, y) => x.order > y.order ? 1 : x.order < y.order ? -1 : 0);
  }

  /**
   * Заполнение form
   */
  protected fillFormGroup() {
    this.form = new FormGroup({});

    for (let i = 0; i < this.data.info.properties.length; i++) {
      const info = this.data.info.properties[i];
      let regularExpressionMask = info.validationsInfo.find(x => x.name.split('_')[0] == 'regularExpression' && x.params.length > 1);
      if (regularExpressionMask != null) {
        this.masks[info.name] = regularExpressionMask.params[1].map((x: string) => x.length > 1 ? new RegExp(x.replace(/\//g, '')) : x);
      }
      let validations: ValidatorFn[] = info.validationsInfo.map(x => this.getValidator(x.name, x.params, info.controlType));

      switch (info.controlType) {
        case 'Input':
          {
            if (info.display) {
              this.form.addControl(
                info.name,
                new FormControl(
                  !this.data.model ? info.defaultValue : this.data.model[info.name],
                  info.validationsInfo.map(x => this.getValidator(x.name, x.params, info.controlType))
                )
              );
            }
            else {
              this.form.addControl(
                info.name,
                new FormControl(!this.data.model ? info.defaultValue : this.data.model[info.name])
              );
            }
          }
          break;
        case 'DateTime':
        case 'Date':
        case 'Time':
        case 'RangeDateTime':
        case 'RangeDate':
        case 'RangeTime':
          {
            let value: Date | [Date, Date];
            let defaultValue: Date | [Date, Date];
            if (this.data.model) {
              switch (info.controlType) {
                case 'Time':
                case 'Date':
                case 'DateTime':
                  value = new Date(this.data.model[info.name]);
                  defaultValue = new Date(info.defaultValue);
                  break;
                case 'RangeTime':
                case 'RangeDate':
                case 'RangeDateTime':
                  value = this.data.model[info.name] && this.data.model[info.name].length ? [new Date(this.data.model[info.name][0]), new Date(this.data.model[info.name][1])] : null;
                  defaultValue = info.defaultValue && info.defaultValue.length ? [new Date(info.defaultValue[0]), new Date(info.defaultValue[1])] : null;
                  break;
              }
            }
            let name = info.controlType.substr(0, 1).toLowerCase() + info.controlType.substr(1);
            if (name.substr(0, 5) == 'range') {
              validations.push()
              this.form.addControl(info.name, new FormControl(!this.data.model ? defaultValue : value, validations));
            }
            else {
              this.form.addControl(info.name, new FormControl(!this.data.model ? defaultValue : value, validations));
            }
          }
          break;
        case 'Autocomplete':
          {
            if (this.data.model && this.data.model[info.name]) {
              this.data.model[info.name].toString = () => this.data.model[info.name].value;
            }
            if (info.defaultValue) {
              info.defaultValue.toString = () => info.defaultValue.value;
            }
            this.form.addControl(info.name, new FormControl(!this.data.model ? info.defaultValue : this.data.model[info.name], validations));
            this.form.controls[info.name].valueChanges.pipe(debounceTime(500)).subscribe(
              (value: any) => {
                if (!value) {
                  this.autocompletes[info.name] = [];
                }
                else if (typeof (value) === 'string') {
                  this.autocomplete(info.name, value);
                }
              }
            );
          }
          break;
        case 'MultipleAutocomplete':
          {
            this.form.addControl(info.name, new FormControl('', validations));
            if (this.data.model && this.data.model[info.name]) {
              for (const item of this.data.model[info.name]) {
                item.toString = () => item.value;
              }
              this.form.controls[info.name].setValue(this.data.model[info.name]);
            }
            if (info.defaultValue) {
              for (const item of info.defaultValue) {
                item.toString = () => item.value;
              }
              this.form.controls[info.name].setValue(info.defaultValue);
            }
            this.multipleAutocompleteForm.addControl(info.name, new FormControl(''));
            this.multipleAutocompleteForm.controls[info.name].valueChanges.pipe(debounceTime(500)).subscribe(
              (value: any) => {
                if (!value) {
                  this.autocompletes[info.name] = [];
                }
                else if (typeof (value) === 'string') {
                  this.autocomplete(info.name, value, true);
                }
              }
            );
          }
          break;
        case 'MultipleFile':
        case 'File':
          {
            validations.push(AtpValidators.checkFiles());
          }
        case 'Number':
        case 'Select':
        case 'MultipleSelect':
        case 'Password':
        case 'Boolean':
        case 'Textarea':
        case 'MultipleFile':
        case 'File':
          {
            this.form.addControl(info.name, new FormControl(!this.data.model ? info.defaultValue : this.data.model[info.name], validations));
          }
          break;
        case 'Custom':
          {
            this.correctResults[info.name] = { action: (result: any) => { } };
          }
          break;
      }
    }

    for (const key in this.form.controls) {
      this.allControls[key] = this.form.controls[key];
    }
  }

  ngOnInit() {
    this.fillFormGroup();
    this.correctPropertiesInfo(this.data.info);
  }

  protected getValidator(name: string, params: any[], type: string): ValidatorFn {
    switch (name.split('_')[0]) {
      case 'required':
        return AtpValidators.required;
      case 'emailAddress':
        return AtpValidators.pattern(/^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,10})$/);
      case 'maxLength':
        return AtpValidators.maxLength(params[0]);
      case 'minLength':
        return AtpValidators.minLength(params[0]);
      case 'stringLength':
        return AtpValidators.stringLength(params[0], params[1]);
      case 'regularExpression':
        return AtpValidators.regularExpression(params[0], name);
      case 'compare':
        return AtpValidators.compare(this.form, params[0]);
      case 'inn':
        return AtpValidators.inn();
      case 'rangeDateTime':
        return AtpValidators.rangeDateTime(this.dateTimeService, type);
    }
  }

  checkItemRequired(fieldInfo: IAtpAddEditPropertyInfo): boolean {
    return fieldInfo.validationsInfo.find(x => x.name == 'required') ? true : false;
  }

  getErrorMessage(fieldInfo: IAtpAddEditPropertyInfo): string {
    if (!this.form || !this.form.controls[fieldInfo.name] || !this.form.controls[fieldInfo.name].invalid) {
      return null;
    }

    for (const key in this.form.controls[fieldInfo.name].errors) {
      if (this.form.controls[fieldInfo.name].errors[key.toLowerCase()]) {
        let error = fieldInfo.validationsInfo.find(x => x.name.toLowerCase() == key);
        // if (fieldInfo.name == 'orgRequest'){
        //   console.log([error, fieldInfo.name, this.form.controls[fieldInfo.name]]);
        // }
        if (error) {
          return error.errorMessage;
        }
      }
    }

    return null;
  }

  submit(): void {
    console.log(this.form);
    this.validation(this.form);

    if (this.form.invalid) return;

    this.sendData(this.correctResultValues(this.form.value));
  }

  protected correctResultValues(object: any): any {
    let result = {};
    for (const group of this.data.info.groups) {
      if (group.type == 'Group' && group.bind) {
        result['isVisible' + group.name] = group.isVisible ? true : false;
      }
      else if (group.type == 'Tabs' && group.params && group.params.length && group.params[0] == 'OnlyActive') {
        result['active' + group.name + 'TabItem'] = group.items.find(x => x.groupName == group.name && (<any>x).isVisible).name;
      }
    }
    for (const item of this.data.info.properties) {
      if (this.form.controls[item.name]) {
        switch (item.controlType) {
          case 'Date':
          case 'Time':
          case 'DateTime':
            result[item.name] = this.dateTimeService.formatStringToDate(object[item.name], item.controlType);
            result[item.name] = result[item.name] ? (<Date>result[item.name]).toUTCString() : result[item.name];
            break;
          case 'RangeDate':
          case 'RangeTime':
          case 'RangeDateTime':
            if (!object[item.name] || object[item.name].length < 2) {
              break;
            }
            result[item.name] = object[item.name][0] && object[item.name][1] ? [object[item.name][0].toUTCString(), object[item.name][1].toUTCString()] : null;
            break;
          case 'Autocomplete':
            result[item.name] = object[item.name] ? { id: object[item.name].id } : null;
            break;
          case 'MultipleAutocomplete':
            result[item.name] = object[item.name] ? object[item.name].map(x => { return { id: x.id.toString() } }) : null;
            break;
          case 'Boolean':
            result[item.name] = object[item.name] ? object[item.name] : false;
            break;
          case 'Input':
          case 'Number':
          case 'Select':
          case 'MultipleSelect':
          case 'Password':
          case 'Textarea':
          case 'MultipleFile':
          case 'File':
          case 'Custom':
            if (object[item.name]) {
              result[item.name] = object[item.name];
            }
            break;
        }
      }
    }

    if (this.data.settings && this.data.settings.isTree) {
      result['parentId'] = this.data.model ? this.data.model.parentId : this.data.params ? this.data.params.parentId : null;
    }

    for (const key in this.correctResults) {
      if (this.correctResults.hasOwnProperty(key)) {
        const element = this.correctResults[key];
        element.action(result);
      }
    }

    return result;
  }

  protected correctResults: { [key: string]: { action: (result: any) => void } } = {};

  protected validation(formGroup: FormGroup) {
    for (const key in formGroup.controls) {
      if (formGroup.controls[key] instanceof FormControl) {
        formGroup.controls[key].markAsTouched();
      }
      else if (formGroup.controls[key] instanceof FormGroup) {
        this.validation(<FormGroup>formGroup.controls[key]);
      }
    }
  }

  /**
   * Отправка данных
   * @param formData
   */
  protected sendData(data: any) {
    this.isBlockedPage = true;
    let apiName = !this.data.settings ? null : !this.data.model ? this.data.settings.addApiName : this.data.settings.editApiName;
    if (!apiName) {
      apiName = (!this.data.model ? 'add' : 'edit') + (this.data.settings && this.data.settings.baseGroupName ? this.data.settings.baseGroupName : this.data.type[0].toUpperCase() + this.data.type.substr(1));
    }

    this.api[apiName](this.data.type, data).subscribe(
      (result: string) => {
        this.isBlockedPage = false;
        if (this.data.settings && this.data.settings.isNotResultPopup) {
          this.confirm(result);
        }
        else {
          let data = new AtpActionsPopupData(this.data.info.title, result, 'Закрыть', null);
          data.successBtnColor = 'primary';
          data.titleColor = '#006600';
          const dialogRef = this.dialog.open(AtpActionsPopup, {
            width: '450px',
            data: data,
          });

          dialogRef.afterClosed().subscribe(resultDialog => {
            this.confirm(resultDialog);
          });
        }
      },
      err => {
        this.httpErrors.process(err.status, () => { this.sendData(data); }, this, [new AtpHttpErrorHandler(400, err.error)]);
      }
    );
  }

  abstract confirm(result: string): void;
  abstract close(): void;

  protected getEntryComponent(name: string) {
    if (this.annotations && this.annotations.entryComponents) {
      if (this.annotations.entryComponents[name.toUpperCase()]) {
        return this.annotations.entryComponents[name.toUpperCase()];
      }
    }
    console.error(name + ' компонент не помещен в entryComponents');
    return null;
  }

  ngOnDestroy() {
    this.titleService.setTitle(this._oldTitle);
  }

  getTemplateName(type: string) {
    return 'inputFormControl';
  }

}
